import {
  Card,
  Text,
  Group,
  Stack,
  ActionIcon,
  Divider,
  Badge,
  Tooltip,
} from "@mantine/core";
import { IconTicket, IconCheck, IconX } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { countries } from "countries-list";
import Flags from "country-flag-icons/react/3x2";
import { useTranslation } from "react-i18next";
import { getTranslatedMonth } from "@congrs/utils";

function TicketCard({ ticket, customFields = [] }) {
  const { t } = useTranslation();
  const fieldTypeMap = customFields.reduce((acc, field) => {
    acc[field.key] = field.type;
    return acc;
  }, {});

  const renderFieldValue = (value, fieldKey) => {
    const fieldType = fieldTypeMap[fieldKey];

    if (fieldType === "country" && value) {
      const country = countries[value.toUpperCase()];
      const FlagComponent = Flags[value.toUpperCase()];
      return (
        <Group gap="xs" align="center" wrap="nowrap" justify="flex-end">
          {FlagComponent && <FlagComponent height="12px" />}
          <Text size="sm" lineClamp={1} title={country ? country.name : value}>
            {country ? country.name : value}
          </Text>
        </Group>
      );
    }

    if (fieldType === "date" && value) {
      return (
        <Text
          size="sm"
          lineClamp={1}
          title={`${String(new Date(value).getDate()).padStart(2, "0")} ${getTranslatedMonth(value, t)} ${new Date(value).getFullYear()}`}
        >
          {`${String(new Date(value).getDate()).padStart(2, "0")} ${getTranslatedMonth(value, t)} ${new Date(value).getFullYear()}`}
        </Text>
      );
    }

    if (typeof value === "boolean") {
      return value ? (
        <Badge
          color="green"
          variant="light"
          leftSection={<IconCheck size={12} />}
        >
          Yes
        </Badge>
      ) : (
        <Badge color="red" variant="light" leftSection={<IconX size={12} />}>
          No
        </Badge>
      );
    } else if (Array.isArray(value)) {
      return (
        <Text size="sm" lineClamp={1} title={value.join(", ")}>
          {value.join(", ")}
        </Text>
      );
    } else {
      return (
        <Text size="sm" lineClamp={1} title={value || "Not provided"}>
          {value || "Not provided"}
        </Text>
      );
    }
  };

  const hasAdditionalFields =
    ticket?.additionalFields && Object.keys(ticket.additionalFields).length > 0;

  // Custom sort for additional fields for ACI FMA
  const sortAdditionalFields = (fields) => {
    const order = [
      "Institution Name",
      "Attend Networking Event",
      "Attend Lunch",
      "Attend Gala Dinner",
    ];
    return fields.sort((a, b) => {
      const indexA = order.indexOf(a[0]);
      const indexB = order.indexOf(b[0]);
      if (indexA === -1 && indexB === -1) {
        return a[0].localeCompare(b[0]);
      }
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Card.Section withBorder inheritPadding py="xs">
        <Group justify="space-between">
          <Text fw={700} size="lg">
            {ticket?.firstName || ""} {ticket?.lastName || ""}
          </Text>
          {ticket?.ticketId && (
            <ActionIcon
              variant="light"
              color="green"
              component={Link}
              title="View ticket details"
              to={`/app/tickets/${ticket.ticketId}`}
            >
              <IconTicket size="1.125rem" />
            </ActionIcon>
          )}
        </Group>
      </Card.Section>

      <Stack mt="md" gap="xs">
        <Group justify="space-between" wrap="nowrap" gap="xl">
          <Text fw={500}>Ticket ID:</Text>
          <Text lineClamp={1} title={ticket?.id}>
            #{ticket?.id}
          </Text>
        </Group>

        <Group justify="space-between" wrap="nowrap" gap="xl">
          <Text fw={500}>Email:</Text>
          <Text lineClamp={1} title={ticket?.email || "N/A"}>
            {ticket?.email || "N/A"}
          </Text>
        </Group>

        <Group justify="space-between" wrap="nowrap" gap="xl">
          <Text fw={500}>Ticket type:</Text>
          {renderFieldValue(ticket?.ticketTypeName)}
        </Group>

        {hasAdditionalFields && (
          <>
            <Divider
              my="xs"
              label="Additional Information"
              labelPosition="center"
            />

            <Stack>
              {sortAdditionalFields(
                Object.entries(ticket.additionalFields)
              ).map(([key, value]) => (
                <Group key={key} justify="space-between" gap="xs" wrap="nowrap">
                  <Tooltip label={key} position="top-start" withinPortal>
                    <Text
                      size="xs"
                      c="dimmed"
                      lineClamp={1}
                      style={{ maxWidth: "60%" }}
                      fw={500}
                    >
                      {key}:
                    </Text>
                  </Tooltip>
                  <div style={{ flexGrow: 1, textAlign: "right" }}>
                    {renderFieldValue(value, key)}
                  </div>
                </Group>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
}

export default TicketCard;
